/**
 * Ledger rules › Spread over — which purchase invoice lines are spread over a period, set once (Journey F;
 * decision 7).
 *
 * A rule is set per supplier or per nominal; each line of a new purchase invoice takes it
 * at capture, and the reviewer can override it in Amend details…, with a reason.
 * The rules are the purchase ledger's, read from its composed view
 * (`/views/spread-over-rules`: each subject named, its history and its use), and
 * searched, filtered and paged there; a change is a new version, posted to the
 * URL that read carried. Whoever may not write ledger rules sees them only.
 */

import { Fragment, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { SpreadOverRuleScope } from "@shared-core/data/accruals-and-prepayments-types";
import { accrualsErrorMessage } from "@shared-core/data/api/accruals-and-prepayments";
import { formatStamp } from "@shared-core/domain/accruals-and-prepayments";
import {
  SPREAD_OVER_WORDS,
  spreadOverAnswer,
} from "@shared-core/domain/spread-over";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { useLoad } from "@shared-core/domain/useLoad";
import { paths } from "@shared-core/routing/routes";
import { ListPagination } from "../../patterns/ListPagination";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Skeleton } from "../../primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { ToggleGroup, ToggleGroupItem } from "../../primitives/toggle-group";
import {
  SpreadOverRuleDialog,
  spreadOverRuleSubjectName,
  type SpreadOverRuleTarget,
} from "./SpreadOverRuleDialog";

const PAGE_SIZE = 50;

type ScopeFilter = "all" | SpreadOverRuleScope;
type SpreadFilter = "either" | "spread" | "not_spread";
const SCOPE_FILTERS: { value: ScopeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "supplier", label: "Supplier" },
  { value: "nominal", label: "Nominal" },
];
const SPREAD_FILTERS: { value: SpreadFilter; label: string }[] = [
  { value: "either", label: "Either" },
  { value: "spread", label: SPREAD_OVER_WORDS.spread },
  { value: "not_spread", label: SPREAD_OVER_WORDS.notSpread },
];

export function SpreadOverRulesTab() {
  const adapter = useDataAdapter();
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<ScopeFilter>("all");
  const [spread, setSpread] = useState<SpreadFilter>("either");
  const [revision, setRevision] = useState(0);
  const [target, setTarget] = useState<SpreadOverRuleTarget | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const q = useDebouncedValue(search.trim(), 250);

  const listKey = JSON.stringify([q, scope, spread, revision]);
  // Changing a filter goes back to page 1 (docs/list-endpoints.md).
  const [paging, setPaging] = useState({ key: listKey, page: 1 });
  const page = paging.key === listKey ? paging.page : 1;
  const rules = useLoad(
    () =>
      adapter.listSpreadOverRules({
        q: q || undefined,
        scope: scope === "all" ? undefined : [scope],
        isSpreadOver: spread === "either" ? undefined : spread === "spread",
        page,
        pageSize: PAGE_SIZE,
      }),
    `${listKey}|${page}`,
  );
  const isFiltered = q !== "" || scope !== "all" || spread !== "either";

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (rules.error && !rules.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Could not load the spread-over rules:{" "}
          {accrualsErrorMessage(rules.error, "the ledger did not answer.")}
        </AlertDescription>
      </Alert>
    );
  }

  const data = rules.data;
  const canManage = data?.canManageRules ?? false;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-muted-foreground max-w-2xl text-sm">
            Which purchase invoice lines are spread over a period. Each line of
            a new purchase invoice gets its {SPREAD_OVER_WORDS.name} when the
            purchase invoice is captured in{" "}
            <Link
              className="underline"
              to={paths.objectList({
                objectId: "finance_purchase_ledger.purchase_invoice",
              })}
            >
              Purchase invoices
            </Link>
            . On each line, the first of these that applies wins:
          </p>
          <ol className="text-muted-foreground mt-2 max-w-2xl list-decimal space-y-0.5 pl-5 text-sm">
            <li>
              <span className="text-foreground">An override on the line</span>,
              made by the reviewer in Amend details… with a reason.
            </li>
            <li>
              <span className="text-foreground">A nominal rule</span> — it says
              what was bought.
            </li>
            <li>
              <span className="text-foreground">A supplier rule.</span>
            </li>
            <li>
              <span className="text-foreground">
                {SPREAD_OVER_WORDS.notSpread}
              </span>
              , when no rule matches.
            </li>
          </ol>
        </div>
        {canManage && (
          <Button onClick={() => setTarget({ mode: "create" })}>
            New rule
          </Button>
        )}
      </div>

      {!data ? (
        <Skeleton className="h-48 w-full" aria-label="Loading the rules" />
      ) : data.total === 0 && !isFiltered ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-8 text-center text-sm">
          No spread-over rules yet, so no purchase invoice line is spread unless
          the reviewer gives it a period in Amend details…. Add one for rent,
          insurance or an annual licence.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:w-64">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search supplier, nominal, reason"
                aria-label="Search spread-over rules"
                className="h-8 pl-8"
              />
            </div>
            <FilterToggle
              label="Applies to"
              options={SCOPE_FILTERS}
              value={scope}
              onChange={setScope}
            />
            <FilterToggle
              label={SPREAD_OVER_WORDS.question}
              options={SPREAD_FILTERS}
              value={spread}
              onChange={setSpread}
            />
          </div>
          <div
            className={
              rules.isLoading
                ? "overflow-x-auto rounded-md border opacity-60 transition-opacity"
                : "overflow-x-auto rounded-md border"
            }
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" aria-label="History" />
                  <TableHead className="min-w-[14rem]">Applies to</TableHead>
                  <TableHead>{SPREAD_OVER_WORDS.question}</TableHead>
                  <TableHead className="text-right">
                    Purchase invoices, 12 months
                  </TableHead>
                  <TableHead className="text-right">Overridden</TableHead>
                  <TableHead className="min-w-[16rem]">Last changed</TableHead>
                  {canManage && (
                    <TableHead className="w-20" aria-label="Actions" />
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canManage ? 7 : 6}
                      className="text-muted-foreground py-6 text-center text-sm"
                    >
                      No rules match these filters.
                    </TableCell>
                  </TableRow>
                )}
                {data.items.map((rule) => {
                  const isOpen = expanded.has(rule.id);
                  return (
                    <Fragment key={rule.id}>
                      <TableRow>
                        <TableCell>
                          {rule.earlierVersions.length > 0 && (
                            <button
                              type="button"
                              onClick={() => toggle(rule.id)}
                              aria-expanded={isOpen}
                              aria-label={`History for ${rule.subjectLabel}`}
                              className="hover:bg-muted rounded p-0.5"
                            >
                              {isOpen ? (
                                <ChevronDown className="size-4" />
                              ) : (
                                <ChevronRight className="size-4" />
                              )}
                            </button>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">
                              {rule.scopeLabel}
                            </Badge>
                            {rule.supplierNo ? (
                              <Link
                                className="font-medium hover:underline"
                                to={paths.supplier({
                                  supplierNo: rule.supplierNo,
                                })}
                              >
                                {rule.subjectLabel}
                              </Link>
                            ) : (
                              <span className="font-medium">
                                {spreadOverRuleSubjectName(rule)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              rule.current.isSpreadOver ? "default" : "neutral"
                            }
                          >
                            {spreadOverAnswer(rule.current.isSpreadOver)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {rule.purchaseInvoicesLast12Months}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {rule.overriddenLast12Months}
                        </TableCell>
                        <TableCell className="text-sm whitespace-normal">
                          {rule.current.reason && (
                            <div>{rule.current.reason}</div>
                          )}
                          <div className="text-muted-foreground text-xs">
                            {rule.current.setByName},{" "}
                            {formatStamp(rule.current.setAt)}
                          </div>
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setTarget({ mode: "edit", rule })}
                              aria-label={`Change the rule for ${rule.subjectLabel}`}
                            >
                              Change
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                      {isOpen &&
                        rule.earlierVersions.map((v) => (
                          <TableRow
                            key={`${rule.id}-${v.setAt}`}
                            className="bg-muted/30 text-muted-foreground hover:bg-muted/30"
                          >
                            <TableCell />
                            <TableCell className="text-xs">
                              Earlier version
                            </TableCell>
                            <TableCell className="text-xs">
                              {spreadOverAnswer(v.isSpreadOver)}
                            </TableCell>
                            <TableCell colSpan={2} />
                            <TableCell className="text-xs whitespace-normal">
                              {v.reason ?? "No reason recorded"} — {v.setByName}
                              , {formatStamp(v.setAt)}
                            </TableCell>
                            {canManage && <TableCell />}
                          </TableRow>
                        ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {data.totalPages > 1 && (
            <ListPagination
              page={page}
              totalPages={data.totalPages}
              onPageChange={(p) => setPaging({ key: listKey, page: p })}
            />
          )}
        </div>
      )}

      {target && data && (
        <SpreadOverRuleDialog
          target={target}
          spreadOverRulesUrl={data.spreadOverRulesUrl}
          onCancel={() => setTarget(null)}
          onSaved={(message) => {
            setTarget(null);
            toast.success(message);
            setRevision((r) => r + 1);
          }}
        />
      )}
    </div>
  );
}

function FilterToggle<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-muted-foreground text-xs">{label}</span>
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        aria-label={label}
        value={value}
        onValueChange={(v) => {
          // Radix emits "" when the active item is clicked again: keep it.
          if (v) onChange(v as T);
        }}
      >
        {options.map((o) => (
          <ToggleGroupItem
            key={o.value}
            value={o.value}
            className="flex-none px-3 text-xs"
          >
            {o.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}
